import { useEffect, useMemo, useRef, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Users, UtensilsCrossed, Wallet, FileDown, Flame } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getGuest } from "@/lib/guest";
import GuestDetailsDialog from "@/components/site/GuestDetailsDialog";
import { useSettings, trackEvent } from "@/lib/settings";
import { Phone } from "lucide-react";

const num = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : d; };
const inr = (n) => `₹${new Intl.NumberFormat("en-IN").format(Math.round(n))}`;
export const prettyDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const Row = ({ label, value, muted, minus, testId }) => (
  <div className={`flex items-baseline justify-between gap-4 ${muted ? "text-ivory/55" : "text-ivory/85"}`} data-testid={testId}>
    <span className="text-sm font-light leading-snug">{label}</span>
    <span className={`text-sm whitespace-nowrap ${minus ? "text-emerald-300" : ""}`}>{minus ? `− ${value}` : value}</span>
  </div>
);

export default function BudgetCalculator({ cfg = {}, range, enquiry, slug, onHold }) {
  const { settings } = useSettings();
  const contactPhone = settings?.contact?.phone || "";
  const days = range.days || 1;
  const gMin = num(cfg.guest_min, 10);
  const gMax = Math.max(num(cfg.guest_max, 300), gMin + 10);
  const [guests, setGuests] = useState(() => Math.min(Math.max(num(cfg.guest_default, 60), gMin), gMax));
  const [includeFood, setIncludeFood] = useState(true);
  const [foodIdx, setFoodIdx] = useState(null);
  const [payIdx, setPayIdx] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    return () => clearTimeout(t);
  }, []);

  const venuePkgs = useMemo(() => (cfg.venue_packages || [])
    .map((p) => ({ label: p.label, days: num(p.days, 1), price: num(p.price) }))
    .filter((p) => p.price > 0)
    .sort((a, b) => a.days - b.days), [cfg]);
  const venue = venuePkgs.find((p) => p.days >= days) || venuePkgs[venuePkgs.length - 1] || null;
  const longer = venue ? days > venue.days : false;

  const foodPkgs = useMemo(() => (cfg.food_packages || [])
    .map((p) => ({ label: p.label, min: num(p.min_pax), rate: num(p.rate), extra: num(p.extra_rate) || num(p.rate) }))
    .filter((p) => p.min > 0 && p.rate > 0)
    .sort((a, b) => a.min - b.min), [cfg]);
  const suggestedIdx = useMemo(() => {
    let s = 0;
    foodPkgs.forEach((p, i) => { if (p.min <= guests) s = i; });
    return s;
  }, [foodPkgs, guests]);
  const fIdx = foodIdx === null ? suggestedIdx : Math.min(foodIdx, Math.max(foodPkgs.length - 1, 0));
  const food = foodPkgs[fIdx] || null;
  const foodTotal = includeFood && food ? (food.min * food.rate + Math.max(0, guests - food.min) * food.extra) * days : 0;

  const payModes = useMemo(() => (cfg.payment_modes || [])
    .map((p) => ({ label: p.label, discount: num(p.discount), note: p.note || "" }))
    .filter((p) => p.label), [cfg]);
  const pay = payModes[Math.min(payIdx, Math.max(payModes.length - 1, 0))] || null;

  const venueTotal = venue ? venue.price : 0;
  const deal = venueTotal + foodTotal;
  const discount = pay ? (deal * pay.discount) / 100 : 0;

  const thr = num(cfg.extra_guest_threshold);
  const chg = num(cfg.extra_guest_charge);
  const extraGuests = thr > 0 && guests > thr ? guests - thr : 0;
  const extraStay = extraGuests && chg > 0 ? extraGuests * chg * days : 0;

  const total = deal - discount + extraStay;

  const setGuestsClamped = (v) => setGuests(Math.min(Math.max(Math.round(num(v, gMin)), gMin), gMax));

  const [downloading, setDownloading] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const requestDownload = () => {
    const g = getGuest();
    if (g) downloadPdf(g);
    else setGuestOpen(true);
  };
  const downloadPdf = async (guest) => {
    setDownloading(true);
    try {
      const payload = {
        property_slug: slug || "",
        start: range.start,
        end: range.end,
        days,
        guests,
        name: guest?.name || "",
        phone: guest?.phone || "",
        email: guest?.email || "",
        venue_label: venue?.label || "",
        venue_total: venueTotal,
        food_included: !!(includeFood && food),
        food_label: food?.label || "",
        food_min_pax: food?.min || 0,
        food_rate: food?.rate || 0,
        food_extra_rate: food?.extra || 0,
        food_total: foodTotal,
        deal_value: deal,
        payment_label: pay?.label || "",
        payment_discount_pct: pay?.discount || 0,
        discount,
        extra_guests: extraGuests,
        extra_threshold: thr,
        extra_charge: chg,
        extra_total: extraStay,
        total,
        disclaimer: cfg.disclaimer || "",
      };
      const res = await api.post("/public/estimate-pdf", payload, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Happy-Retreats-Estimate-${range.start}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your estimate PDF is downloading");
    } catch (e) {
      toast.error("Could not prepare the PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const enquire = () => {
    const lines = [
      `Selected dates: ${prettyDate(range.start)} → ${prettyDate(range.end)} (${days} day${days === 1 ? "" : "s"})`,
      `Guests: ~${guests}`,
      venue ? `Venue (${venue.label || `${venue.days}-day package`}): ${inr(venueTotal)}` : null,
      includeFood && food ? `Food (${food.label}): ${inr(foodTotal)}` : "Food: not included",
      extraStay ? `Extra accommodation (${extraGuests} guests above ${thr}): ${inr(extraStay)}` : null,
      pay ? `Payment mode: ${pay.label} (${pay.discount}% off) — saving ${inr(discount)}` : null,
      `Estimated total: ${inr(total)} (incl. GST)`,
    ].filter(Boolean).join("\n");
    enquiry?.open?.(slug, { preferred_date: range.start, guest_count: String(guests), message: lines });
  };

  return (
    <div ref={ref} className="calc-enter scroll-mt-28" data-testid="budget-calculator">
      <div className="border border-ink/10 bg-white shadow-[0_30px_80px_-50px_rgba(0,0,0,0.35)]">
        <div className="px-6 md:px-10 py-6 md:py-7 border-b border-ink/10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline accent-color">Step 2 · Budget calculator</div>
            <div className="font-display text-2xl md:text-3xl mt-2">Craft your estimate</div>
          </div>
          <div className="text-left sm:text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-graphite">Your dates</div>
            <div className="font-medium mt-1" data-testid="calc-dates">{prettyDate(range.start)} → {prettyDate(range.end)}</div>
            <div className="text-xs text-graphite mt-0.5">
              {days * 24} hours · {days} day{days === 1 ? "" : "s"}{venue?.label ? ` · ${venue.label} venue package` : ""}
            </div>
            {longer && <div className="text-[11px] text-clay mt-1">Longer than our largest package — our planners will confirm final venue pricing.</div>}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-7 px-6 md:px-10 py-8 md:py-10 space-y-10">
            <div data-testid="calc-guests-block">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                  <Users className="w-4 h-4 accent-color" />
                  <span className="text-sm font-medium uppercase tracking-[0.14em]">Guest count</span>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <input
                    type="number"
                    value={guests}
                    min={gMin}
                    max={gMax}
                    onChange={(e) => setGuestsClamped(e.target.value)}
                    className="w-20 border border-ink/15 bg-ivory px-2 py-1.5 text-right font-display text-xl focus:outline-none focus:border-ink"
                    data-testid="calc-guests-input"
                  />
                  <span className="text-xs text-graphite">guests</span>
                </div>
              </div>
              <Slider
                value={[guests]}
                min={gMin}
                max={gMax}
                step={1}
                onValueChange={([v]) => setGuests(v)}
                className="mt-5"
                data-testid="calc-guests-slider"
              />
              {thr > 0 && chg > 0 && (
                <p className="text-xs text-graphite mt-3 leading-relaxed">
                  An additional {inr(chg)}/person/day applies for guests above {thr} (incl. GST). This is excluded from payment discounts.
                </p>
              )}
            </div>

            {foodPkgs.length > 0 && (
              <div data-testid="calc-food-block">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2.5">
                    <UtensilsCrossed className="w-4 h-4 accent-color" />
                    <span className="text-sm font-medium uppercase tracking-[0.14em]">Food package</span>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-graphite">
                    Include food
                    <Switch checked={includeFood} onCheckedChange={setIncludeFood} data-testid="calc-food-toggle" />
                  </label>
                </div>
                {includeFood && (
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {foodPkgs.map((p, i) => {
                      const active = i === fIdx;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setFoodIdx(i)}
                          className={`relative text-left p-4 border transition-all duration-300 ${active ? "border-[var(--prop-accent)] bg-bone/50 -translate-y-0.5 shadow-sm" : "border-ink/10 hover:border-ink/30"}`}
                          data-testid={`calc-food-pkg-${i}`}
                        >
                          {i === suggestedIdx && (
                            <span className="absolute -top-2 right-3 text-[9px] uppercase tracking-[0.16em] px-2 py-0.5 bg-ink text-ivory">Suggested</span>
                          )}
                          <div className="text-sm font-medium">{p.label}</div>
                          <div className="text-[11px] text-graphite mt-0.5">Min guarantee: {p.min} pax</div>
                          <div className="font-display text-xl mt-2">{inr(p.rate)}<span className="text-xs text-graphite font-body"> /head/day</span></div>
                          <div className="text-[11px] text-graphite mt-1">Additional: {inr(p.extra)}/head/day</div>
                        </button>
                      );
                    })}
                  </div>
                )}
                {includeFood && (
                  <p className="text-xs text-graphite mt-3 leading-relaxed">
                    Per head, per day — covers all meals during your stay. Not sure about final numbers? Start with a minimum guarantee; we refine together.
                  </p>
                )}
              </div>
            )}

            {payModes.length > 0 && (
              <div data-testid="calc-payment-block">
                <div className="flex items-center gap-2.5">
                  <Wallet className="w-4 h-4 accent-color" />
                  <span className="text-sm font-medium uppercase tracking-[0.14em]">Payment mode benefits</span>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {payModes.map((p, i) => {
                    const active = i === payIdx;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setPayIdx(i)}
                        className={`text-left p-4 border transition-all duration-300 ${active ? "border-[var(--prop-accent)] bg-bone/50 -translate-y-0.5 shadow-sm" : "border-ink/10 hover:border-ink/30"}`}
                        data-testid={`calc-pay-${i}`}
                      >
                        <div className="text-sm font-medium">{p.label}</div>
                        <div className="font-display text-2xl mt-1 accent-color">{p.discount}% <span className="text-sm text-ink font-body">off</span></div>
                        {p.note && <div className="text-[11px] text-graphite mt-1">({p.note})</div>}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-graphite mt-3">* Discount is calculated on the total deal value (venue + food).</p>
              </div>
            )}
          </div>

          <div className="lg:col-span-5 bg-ink text-ivory px-6 md:px-10 py-8 md:py-10 flex flex-col" data-testid="calc-breakdown">
            <div className="overline text-ivory/50">Budget breakdown</div>
            <div className="mt-6 space-y-3.5">
              <Row label={`Venue${venue?.label ? ` — ${venue.label}` : ""} (incl. GST)`} value={venueTotal ? inr(venueTotal) : "—"} testId="calc-line-venue" />
              {includeFood && food ? (
                <>
                  <Row muted label={`${food.label} · ${food.min} pax × ${inr(food.rate)} × ${days}d`} value="" />
                  {guests > food.min && <Row muted label={`+ ${guests - food.min} additional × ${inr(food.extra)} × ${days}d`} value="" />}
                  <Row label="Food total" value={inr(foodTotal)} testId="calc-line-food" />
                </>
              ) : (
                <Row muted label="Food not included" value="—" testId="calc-line-food" />
              )}
              <div className="border-t border-ivory/15 pt-3.5">
                <Row label="Total deal value" value={inr(deal)} testId="calc-line-deal" />
              </div>
              {pay && <Row minus label={`Discount — ${pay.label} (${pay.discount}%)`} value={inr(discount)} testId="calc-line-discount" />}
              {extraStay > 0 && <Row label={`Extra accommodation · ${extraGuests} × ${inr(chg)} × ${days}d`} value={inr(extraStay)} testId="calc-line-extra" />}
            </div>
            <div className="border-t border-ivory/20 mt-6 pt-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-ivory/50">Estimated total</div>
              <div className="font-display text-4xl md:text-5xl mt-2" data-testid="calc-total">{inr(total)}</div>
              <p className="text-[11px] text-ivory/45 mt-3 leading-relaxed">{cfg.disclaimer || "Approximate estimate. All prices inclusive of GST. Final pricing may vary."}</p>
            </div>
            <button
              type="button"
              onClick={enquire}
              className="mt-8 w-full bg-ivory text-ink px-8 py-4 text-xs uppercase tracking-[0.22em] font-medium hover:bg-[var(--prop-accent)] hover:text-white transition-colors"
              data-testid="calc-enquire-btn"
            >
              Enquire with this estimate
            </button>
            <button
              type="button"
              onClick={requestDownload}
              disabled={downloading}
              className="mt-3 w-full border border-ivory/30 text-ivory px-8 py-3.5 text-xs uppercase tracking-[0.22em] font-medium inline-flex items-center justify-center gap-2 hover:bg-ivory/10 transition-colors disabled:opacity-60"
              data-testid="calc-download-pdf-btn"
            >
              <FileDown className="w-4 h-4" /> {downloading ? "Preparing PDF…" : "Download quotation (PDF)"}
            </button>
            {onHold && (
              <button
                type="button"
                onClick={() => onHold(`${inr(total)} (incl. GST) · ${days} day${days === 1 ? "" : "s"}, ~${guests} guests`)}
                className="mt-3 w-full border px-8 py-3.5 text-xs uppercase tracking-[0.22em] font-medium inline-flex items-center justify-center gap-2 transition-colors hover:bg-white/10"
                style={{ borderColor: "#F97316", color: "#FDBA74" }}
                data-testid="calc-hold-btn"
              >
                <Flame className="w-4 h-4 fill-current" /> Hold these dates · free for 48 hrs
              </button>
            )}
            {contactPhone && (
              <a
                href={`tel:${contactPhone.replace(/\s+/g, "")}`}
                onClick={() => trackEvent("call_click", { location: "budget_calculator" })}
                className="mt-4 inline-flex items-center justify-center gap-2 w-full text-xs text-ivory/60 hover:text-ivory transition-colors"
                data-testid="calc-call-link"
              >
                <Phone className="w-3.5 h-3.5" /> Prefer to talk? Call us — {contactPhone}
              </a>
            )}
            <GuestDetailsDialog
              open={guestOpen}
              onClose={() => setGuestOpen(false)}
              onSubmit={(g) => { setGuestOpen(false); downloadPdf(g); }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
